import { makeRequest } from '@/network/api';
import { Box, Button, Card, Flex, Heading, HStack, IconButton, Input, Stack, Table, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';
import { useLocation } from 'react-router';

type PointStats = {
  walletAddress: string;
  stats: {
    bridgedTotal: number;
    swapVolume: number;
    swapCount: number;
    tvlTotalUsd: number;
    bridgeVolPoints: number;
    swapVolPoints: number;
    protocolsUsed: number;
    tvlStreakPoints24h: number;
    distinctProtocolPoints: number;
    swapStreakPoints: number;
    longestSwapStreakWeeks: number;
    tvlStreakPoints: number;
    protocolStreakPoints: number;
    currentTvlLevels: string;
    longestTvlStreak: number;
    protocol1: string;
    days_used1: number;
    protocol2: string;
    days_used2: number;
    protocol3: string;
    days_used3: number;
    questPoints: number;
    socialTierPoints: number;
    dailySpinRewardPoints: number;
    miscPoints: number;
    userSelfXp: number;
    referralBonusXp: number;
    totalXp: number;
    xpRank: number;
    [key: string]: number | string | null;
  } | null;
};

const pointAttributes = [
  { label: 'Total XP', key: 'totalXp' },
  { label: 'XP Rank', key: 'xpRank' },
  { label: 'Self XP', key: 'userSelfXp' },
  { label: 'Referral Bonus XP', key: 'referralBonusXp' },
  { label: 'Bridge Volume', key: 'bridgedTotal' },
  { label: 'Bridge Volume Points', key: 'bridgeVolPoints' },
  { label: 'Swap Volume', key: 'swapVolume' },
  { label: 'Swap Count', key: 'swapCount' },
  { label: 'Swap Volume Points', key: 'swapVolPoints' },
  { label: 'TVL Total USD', key: 'tvlTotalUsd' },
  { label: 'TVL Streak Points (24h)', key: 'tvlStreakPoints24h' },
  { label: 'TVL Streak Points (weekly)', key: 'tvlStreakPoints' },
  { label: 'Longest TVL Streak', key: 'longestTvlStreak' },
  { label: 'Current TVL Levels', key: 'currentTvlLevels' },
  { label: 'Protocols Used', key: 'protocolsUsed' },
  { label: 'Distinct Protocol Points', key: 'distinctProtocolPoints' },
  { label: 'Longest Swap Streak (Weeks)', key: 'longestSwapStreakWeeks' },
  { label: 'Swap Streak Points', key: 'swapStreakPoints' },
  { label: 'Protocol Streak Points', key: 'protocolStreakPoints' },
  { label: 'Daily Protocol Points', key: 'dailyProtocolInteractionPoints' },
  { label: 'Protocol 1', key: 'protocol1' },
  { label: 'Days Used (Protocol 1)', key: 'daysUsed1' },
  { label: 'Protocol 2', key: 'protocol2' },
  { label: 'Days Used (Protocol 2)', key: 'daysUsed2' },
  { label: 'Protocol 3', key: 'protocol3' },
  { label: 'Days Used (Protocol 3)', key: 'daysUsed3' },
  { label: 'Quest Points', key: 'questPoints' },
  { label: 'Social Tier Points', key: 'socialTierPoints' },
  { label: 'Daily Spin Reward Points', key: 'dailySpinRewardPoints' },
  { label: 'Misc Points', key: 'miscPoints' },
  { label: 'Plume Staking Points', key: 'plumeStakingPointsEarned' },
  { label: 'PS Bonus Points', key: 'plumeStakingBonusPointsEarned' },
];

export const PointsCompare = () => {
  const location = useLocation();
  const [walletsData, setWalletsData] = useState<PointStats[]>([]);
  const [walletInputs, setWalletInputs] = useState<string[]>(Array(6).fill(''));
  const [isLoading, setIsLoading] = useState(false);

  // Parse query parameters on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Check for wallets in query parameters
    const walletParams = params.getAll('wallet');

    // If there are wallet parameters, use them to initialize walletInputs
    if (walletParams.length > 0) {
      // Create a new array with 6 slots
      const newWallets = Array(6).fill('');

      // Fill with values from parameters (up to 6)
      walletParams.forEach((wallet, index) => {
        if (index < 6) {
          newWallets[index] = wallet;
        }
      });

      setWalletInputs(newWallets);

      // Automatically fetch data if wallets are provided via URL
      fetchWalletData(newWallets);
    }
  }, [location.search]);

  const handleInputChange = (index: number, value: string) => {
    const newInputs = [...walletInputs];
    newInputs[index] = value;
    setWalletInputs(newInputs);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      fetchWalletData();
    }
  };

  const clearWallet = (index: number) => {
    const newInputs = [...walletInputs];
    newInputs[index] = '';
    setWalletInputs(newInputs);

    // Also clear the data for this index
    const newWalletsData = [...walletsData];
    if (newWalletsData[index]) {
      newWalletsData[index] = { walletAddress: '', stats: null };
      setWalletsData(newWalletsData);
    }
  };

  const fetchWalletData = useCallback(
    async (wallets: string[] = walletInputs) => {
      try {
        setIsLoading(true);

        // Filter out empty wallet addresses
        const validWallets = wallets.filter((wallet) => wallet.trim() !== '');

        if (validWallets.length === 0) {
          return;
        }

        const queryParams = validWallets.map((wallet) => `wallets=${encodeURIComponent(wallet)}`).join('&');
        const response = await makeRequest(`/admin/points/walletStats?${queryParams}`);
        const data = await response.json();

        // Map the response to match the wallet inputs order
        const mappedData: PointStats[] = wallets.map((inputWallet) => {
          if (!inputWallet.trim()) {
            return { walletAddress: '', stats: null };
          }

          const walletData = data.data?.walletStats.find(
            (w: PointStats) => w.walletAddress.toLowerCase() === inputWallet.toLowerCase(),
          );

          return walletData || { walletAddress: inputWallet, stats: null };
        });

        setWalletsData(mappedData);
      } catch (error) {
        console.error('Error fetching wallet data:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [walletInputs],
  );

  // Generate the current URL with wallets as query parameters
  const generateWalletUrl = () => {
    const validWallets = walletInputs.filter((wallet) => wallet.trim() !== '');
    if (validWallets.length === 0) return '';

    const baseUrl = `${window.location.origin}/admin/points/compare`;
    const queryParams = validWallets.map((wallet) => `wallet=${encodeURIComponent(wallet)}`).join('&');

    return `${baseUrl}?${queryParams}`;
  };

  const copyUrlToClipboard = () => {
    const url = generateWalletUrl();
    if (!url) return;

    navigator.clipboard
      .writeText(url)
      .then(() => {
        alert('URL copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy URL: ', err);
      });
  };

  return (
    <Box p={0} maxW="1200px" mx="auto">
      <Heading mb={6}>Compare Wallet Points</Heading>

      <Card.Root p={3} mb={6}>
        <Card.Body>
          <Stack gap={3}>
            <Flex flexWrap="wrap" gap={2}>
              {walletInputs.map((wallet, index) => (
                <Flex key={index} direction="column" width={['100%', '48%', '31%']} mb={2}>
                  <Text mb={1} fontSize="xs" fontWeight="medium">
                    Wallet {index + 1}
                  </Text>
                  <Input
                    value={wallet}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    placeholder="Enter wallet address"
                    onKeyPress={handleKeyPress}
                    size="sm"
                  />
                </Flex>
              ))}
            </Flex>

            <HStack alignSelf="flex-start" gap={4} mt={1}>
              <Button colorScheme="blue" onClick={() => fetchWalletData()} disabled={isLoading} size="sm">
                <HStack>
                  {isLoading ? (
                    <Text>Fetching data...</Text>
                  ) : (
                    <>
                      <FiSearch />
                      <Text>Fetch Points Data</Text>
                    </>
                  )}
                </HStack>
              </Button>

              {walletsData.length > 0 && (
                <Button onClick={copyUrlToClipboard} variant="outline" size="sm">
                  Copy Sharable URL
                </Button>
              )}
            </HStack>
          </Stack>
        </Card.Body>
      </Card.Root>

      {walletsData.length > 0 && (
        <Box overflowX="auto">
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Attribute</Table.ColumnHeader>
                {walletsData.map(
                  (wallet, idx) =>
                    wallet.walletAddress && (
                      <Table.ColumnHeader key={idx} textDecoration="underline">
                        <Flex alignItems="center">
                          {wallet.walletAddress.substring(0, 6)}...
                          {wallet.walletAddress.substring(wallet.walletAddress.length - 4)}
                          <IconButton
                            ml={2}
                            size="xs"
                            aria-label="Remove wallet"
                            onClick={() => clearWallet(idx)}
                            variant="ghost"
                          >
                            <FiX />
                          </IconButton>
                        </Flex>
                      </Table.ColumnHeader>
                    ),
                )}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {pointAttributes.map((attr) => (
                <Table.Row key={attr.key} bgColor={attr.label.toLowerCase().includes('xp') ? 'gray.100' : 'white'}>
                  <Table.Cell fontWeight="medium">{attr.label}</Table.Cell>
                  {walletsData.map(
                    (wallet, idx) =>
                      wallet.walletAddress && (
                        <Table.Cell
                          key={idx}
                          fontWeight={attr.key.toLowerCase().includes('points') ? 'bold' : 'normal'}
                        >
                          {attr.key.toLowerCase().includes('usd') || attr.label.toLowerCase().endsWith('volume')
                            ? '$'
                            : ''}
                          {wallet.stats
                            ? attr.key === 'currentTvlLevels'
                              ? wallet.stats[attr.key] || '-'
                              : typeof wallet.stats[attr.key] === 'number'
                                ? wallet.stats[attr.key]?.toLocaleString() || '-'
                                : wallet.stats[attr.key] || '-'
                            : '-'}
                        </Table.Cell>
                      ),
                  )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </Box>
  );
};
